import { Link } from "react-router-dom";
import { ArrowUpRight, Mic, Users, Compass, BookOpenCheck, Quote } from "lucide-react";
import { BRAND, TESTIMONIALS, PRODUCTS } from "@/content";

export default function Home() {
  return (
    <div data-testid="page-home" className="bg-white">
      {/* HERO */}
      <section className="relative pt-[120px] pb-16 lg:pb-24 overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-7 rise">
              <p className="overline" data-testid="hero-eyebrow">A movement, not a moment</p>
              <h1 className="hero-display mt-6">
                It's<br/>not&nbsp;too<br/>late<br/><em className="not-italic font-serif text-[var(--ic-accent)]">to&nbsp;choose</em><br/>you.
              </h1>
            </div>

            <div className="col-span-12 lg:col-span-5 lg:pl-6">
              <div className="zoom-img-wrap relative overflow-hidden border border-[var(--ic-line)] aspect-[4/5] bg-[var(--ic-warm)]">
                <img
                  src={BRAND.images.headshot}
                  alt="Crystal Purdue, founder of The I Choose Movement"
                  className="zoom-img absolute inset-0 w-full h-full object-cover object-top"
                  data-testid="hero-headshot"
                />
              </div>
              <div className="mt-6 hairline pt-6 flex items-start justify-between gap-6">
                <p className="font-sans text-[15px] leading-relaxed text-black/75 max-w-sm">
                  I'm Crystal Purdue. I help women and young people discover the power of
                  intentional choices &mdash; and create lives they actually want to live.
                </p>
                <span className="overline whitespace-nowrap shrink-0">Est. Movement</span>
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link to="/speaking#inquiry" data-testid="hero-cta-speak" className="btn-ink">
              Book Crystal to Speak <ArrowUpRight size={16} />
            </Link>
            <Link to="/about" data-testid="hero-cta-journey" className="btn-ghost">
              Start Your Journey
            </Link>
            <a href={BRAND.calendly} target="_blank" rel="noreferrer" data-testid="hero-cta-coach" className="ed-link ml-2">
              Or book a consultation
            </a>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section aria-hidden className="border-y border-[var(--ic-line)] bg-[var(--ic-warm)] py-6 overflow-hidden">
        <div className="marquee-track">
          {[...Array(2)].map((_, j) => (
            <div key={j} className="flex items-center gap-16 pr-16">
              {["I choose me.", "I choose voice.", "I choose now.", "I choose enough.", "I choose freedom.", "I choose forward.", "I choose us."].map((w, i) => (
                <span key={`${j}-${i}`} className="font-serif text-[44px] md:text-[64px] leading-none tracking-tight whitespace-nowrap">
                  {w} <span className="text-[var(--ic-accent)]">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PARTNERS */}
      <section className="py-14 lg:py-16 border-b border-[var(--ic-line)]" data-testid="section-partners">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-3">
              <p className="overline">In good company</p>
              <p className="font-serif text-[22px] md:text-[26px] leading-tight mt-3">
                Featured at &amp; in partnership with.
              </p>
            </div>
            <ul className="col-span-12 lg:col-span-9 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 items-center">
              <li className="flex flex-col gap-1 border-l border-[var(--ic-line)] pl-6">
                <span className="font-serif text-[22px] md:text-[26px] leading-tight">PNC Bank</span>
                <span className="overline">I Choose Financial Freedom</span>
              </li>
              <li className="flex flex-col gap-1 border-l border-[var(--ic-line)] pl-6">
                <span className="font-serif text-[22px] md:text-[26px] leading-tight">Butler College Prep</span>
                <span className="overline">Hello Gorgeous &middot; May 2026</span>
              </li>
              <li className="flex flex-col gap-1 border-l border-[var(--ic-line)] pl-6">
                <span className="font-serif text-[22px] md:text-[26px] leading-tight">For the Love of Me</span>
                <span className="overline">Feb 2026</span>
              </li>
              <li className="flex flex-col gap-1 border-l border-[var(--ic-line)] pl-6">
                <span className="font-serif text-[22px] md:text-[26px] leading-tight">Your stage next?</span>
                <Link to="/speaking#inquiry" className="ed-link text-[11px] self-start mt-1">Book Crystal</Link>
              </li>
            </ul>
          </div>
        </div>
      </section>


      {/* THE MOVEMENT */}
      <section className="py-24 lg:py-32" data-testid="section-movement">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">The Movement</p>
            <h2 className="section-display mt-6">What we believe.</h2>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-6 space-y-8 font-sans text-[17px] leading-[1.75] text-black/80">
            <p>
              The I Choose Movement is where we are choosing to create the life we want to
              live &mdash; and then actually live it. It is where we stop being spectators
              in our own lives and start being <em>participants</em>.
            </p>
            <p>
              It is where we are intentional. Where we create our lives on purpose, by hand.
              Where we keep choosing &mdash; and choosing &mdash; until our I Choose choose
              us back.
            </p>
            <p className="font-serif text-[28px] md:text-[34px] leading-[1.15] text-black pt-4 border-l-2 border-black pl-6">
              You have a right to choose. You don't have to allow life to choose for you.
              You can choose <em>in</em> it.
            </p>
          </div>
        </div>
      </section>

      {/* CRYSTAL'S STORY */}
      <section className="bg-[var(--ic-warm)] py-24 lg:py-32 border-y border-[var(--ic-line)]" data-testid="section-story">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 lg:col-span-5">
            <div className="zoom-img-wrap relative overflow-hidden border border-[var(--ic-line)] aspect-[3/4] bg-white">
              <img
                src={BRAND.images.portraitJoy}
                alt="Crystal Purdue holding an I Choose card"
                className="zoom-img absolute inset-0 w-full h-full object-cover object-center"
              />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p className="overline">Meet Crystal</p>
            <h2 className="section-display mt-6">
              For twenty years<br/>I didn't know &mdash;<br/>then I <em className="font-serif text-[var(--ic-accent)]">chose myself</em>.
            </h2>
            <p className="font-sans text-[17px] leading-[1.75] text-black/80 mt-8 max-w-xl">
              I was married for twenty years and I did not know I was allowed to choose me.
              The day I understood that I could choose myself was the day I found my power.
              Choosing me didn't just give me back to me &mdash; it gave me back to my
              children, too.
            </p>
            <div className="mt-8">
              <Link to="/about" data-testid="story-cta-learn" className="ed-link">Learn More</Link>
            </div>
          </div>
        </div>
      </section>

      {/* HOW WE HELP - 4 pillars */}
      <section className="py-24 lg:py-32" data-testid="section-pillars">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <p className="overline">How we help</p>
              <h2 className="section-display mt-4">Four ways forward.</h2>
            </div>
            <p className="font-sans text-[15px] text-black/65 max-w-sm">
              Every entry point leads to the same place: a life designed on purpose,
              by the person living it.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            <PillarCard to="/speaking#inquiry" icon={<Mic size={20} />} num="01" title="Speaking" desc="Keynotes that move organizations, schools, and women's rooms from inspiration to intention." cta="Book Crystal" testid="pillar-speaking"/>
            <PillarCard to="/workshops#request" icon={<Users size={20} />} num="02" title="Workshops" desc="Half-day and full-day experiences for women, youth, schools, and wellness communities." cta="Request a Workshop" testid="pillar-workshops"/>
            <PillarCard to="/coaching" icon={<Compass size={20} />} num="03" title="Coaching" desc="Private coaching for women ready to stop performing and start choosing." cta="Book a Consultation" testid="pillar-coaching"/>
            <PillarCard to="/shop"     icon={<BookOpenCheck size={20} />} num="04" title="Products" desc="Affirmation decks and planners designed around intentional choosing." cta="Shop I Choose" testid="pillar-products"/>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-black text-white py-24 lg:py-32 relative overflow-hidden" data-testid="section-testimonials">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-4">
              <p className="overline text-white/55">In their words</p>
              <h2 className="section-display mt-4 text-white">Proof,<br/>in voices.</h2>
            </div>
            <div className="col-span-12 lg:col-span-8 grid sm:grid-cols-2 gap-6">
              {TESTIMONIALS.map((t, i) => (
                <figure key={i} className="border border-white/15 p-8" data-testid={`testimonial-${i}`}>
                  <Quote size={22} className="text-white/40" />
                  <blockquote className="font-serif text-[22px] leading-[1.3] mt-4 text-white">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="mt-6 overline text-white/55">
                    {t.name} — {t.role}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE MOVEMENT IN MOTION - real event photos */}
      <section className="py-24 lg:py-32 border-b border-[var(--ic-line)]" data-testid="section-gallery">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <p className="overline">The movement in motion</p>
              <h2 className="section-display mt-4">Real rooms.<br/>Real choosers.</h2>
            </div>
            <p className="font-sans text-[15px] text-black/65 max-w-sm">
              Glimpses from recent workshops, women's gatherings, and high-school sessions
              led by The I Choose Movement.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-4 lg:gap-6">
            <figure className="col-span-12 md:col-span-6 lg:col-span-5 zoom-img-wrap relative overflow-hidden border border-[var(--ic-line)] aspect-[4/5] bg-[var(--ic-warm)]" data-testid="gallery-school">
              <img src={BRAND.images.workshop} alt="Hello Gorgeous Event at Butler College Prep" className="zoom-img absolute inset-0 w-full h-full object-cover" />
              <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/65 to-transparent text-white">
                <p className="overline text-white/85">Hello Gorgeous &middot; May 2026</p>
                <p className="font-serif text-[20px] mt-1">Butler College Prep.</p>
              </figcaption>
            </figure>

            <figure className="col-span-12 md:col-span-6 lg:col-span-4 zoom-img-wrap relative overflow-hidden border border-[var(--ic-line)] aspect-[4/5] bg-[var(--ic-warm)]" data-testid="gallery-audience">
              <img src={BRAND.images.eventAudience} alt="For the Love of Me event audience" className="zoom-img absolute inset-0 w-full h-full object-cover" />
              <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/65 to-transparent text-white">
                <p className="overline text-white/85">For the Love of Me &middot; Feb 2026</p>
                <p className="font-serif text-[20px] mt-1">A room that's listening on purpose.</p>
              </figcaption>
            </figure>

            <figure className="col-span-12 lg:col-span-3 zoom-img-wrap relative overflow-hidden border border-[var(--ic-line)] aspect-[4/5] bg-[var(--ic-warm)]" data-testid="gallery-tee">
              <img src={BRAND.images.eventTee} alt="I Choose Financial Freedom event with PNC Bank" className="zoom-img absolute inset-0 w-full h-full object-cover" />
              <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/65 to-transparent text-white">
                <p className="overline text-white/85">I Choose Financial Freedom</p>
                <p className="font-serif text-[20px] mt-1">In partnership with PNC Bank.</p>
              </figcaption>
            </figure>

            <figure className="col-span-12 zoom-img-wrap relative overflow-hidden border border-[var(--ic-line)] aspect-[21/9] bg-[var(--ic-warm)]" data-testid="gallery-group">
              <img src={BRAND.images.eventGroup} alt="Group at an I Choose event" className="zoom-img absolute inset-0 w-full h-full object-cover object-[center_30%]" />
              <figcaption className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/65 to-transparent text-white">
                <p className="overline text-white/85">I Choose Event</p>
                <p className="font-serif text-[24px] mt-1">Choosing &mdash; until our I Choose choose us back.</p>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>


      {/* FEATURED PRODUCTS */}
      <section className="py-24 lg:py-32" data-testid="section-products">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <p className="overline">Tools for choosing you</p>
              <h2 className="section-display mt-4">Featured products.</h2>
            </div>
            <Link to="/shop" data-testid="products-view-all" className="ed-link">View all products</Link>
          </div>

          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            <div className="col-span-12 lg:col-span-6 zoom-img-wrap relative overflow-hidden border border-[var(--ic-line)] aspect-[4/3] bg-[var(--ic-warm)]">
              <img src={BRAND.images.products} alt="I Choose affirmation cards" className="zoom-img absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/55 to-transparent">
                <p className="overline text-white/85">Bestseller</p>
                <h3 className="font-serif text-white text-2xl mt-1">The Original Affirmation Deck</h3>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {PRODUCTS.slice(0, 4).map((p, i) => (
                <a key={i} href={p.href} target="_blank" rel="noreferrer" data-testid={`featured-product-${i}`}
                   className="border border-[var(--ic-line)] p-6 flex flex-col justify-between hover:bg-[var(--ic-warm)] transition-colors">
                  <div>
                    <p className="overline">0{i+1}</p>
                    <h3 className="font-serif text-[22px] leading-tight mt-3">{p.title}</h3>
                    <p className="text-[14px] text-black/65 mt-2">{p.desc}</p>
                  </div>
                  <span className="ed-link mt-6 self-start text-[11px]">Shop on Square <ArrowUpRight size={14} className="inline -mt-0.5" /></span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[var(--ic-warm-2)] border-y border-[var(--ic-line)]" data-testid="section-final-cta">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 text-center">
          <p className="overline">Your next step</p>
          <h2 className="display mt-6 text-[44px] md:text-[88px]">
            Choose yourself. <em className="font-serif text-[var(--ic-accent)]">Today.</em>
          </h2>
          <p className="font-sans text-[17px] text-black/75 mt-6 max-w-xl mx-auto">
            Bring Crystal to your stage, your team, or your own quiet corner.
            One choice is all it takes to start.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/speaking#inquiry" data-testid="final-cta-speak" className="btn-ink">Book Crystal to Speak</Link>
            <a href={BRAND.calendly} target="_blank" rel="noreferrer" data-testid="final-cta-coach" className="btn-ghost">Book a Consultation</a>
          </div>
        </div>
      </section>
    </div>
  );
}

function PillarCard({ to, icon, num, title, desc, cta, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="col-span-12 sm:col-span-6 lg:col-span-3 group border border-[var(--ic-line)] p-8 flex flex-col justify-between min-h-[320px] bg-white hover:bg-[var(--ic-warm)] transition-colors">
      <div>
        <div className="flex items-center justify-between">
          <span className="overline">{num}</span>
          <span className="text-black/70 group-hover:text-black">{icon}</span>
        </div>
        <h3 className="font-serif text-[32px] mt-8 leading-none">{title}</h3>
        <p className="text-[14.5px] text-black/70 mt-4 leading-relaxed">{desc}</p>
      </div>
      <span className="ed-link mt-8 self-start text-[11px]">{cta} <ArrowUpRight size={14} className="inline -mt-0.5" /></span>
    </Link>
  );
}
